import { useState, useCallback, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Search, Plus, CheckSquare, List, Columns, X, Loader2,
  Trash2, Edit3, Calendar, Clock, AlertTriangle, ChevronRight,
  User as UserIcon, Archive, MousePointerClick, ChevronDown, UserCheck, UserPlus,
} from 'lucide-react'
import { apiClient, clientsApi, projectsApi, usersApi, departmentsApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import { TaskKanban } from '@/components/tasks/TaskKanban'
import { Badge } from '@/components/common/Badge'
import { ErrorState } from '@/components/common/States'
import { cn } from '@/lib/utils'
import type { Task, TaskStatus, TaskPriority, PaginatedResponse } from '@/types'

// --- Helpers ---

function extractArray(raw: unknown): any[] {
  const r = raw as any
  if (Array.isArray(r)) return r
  if (Array.isArray(r?.data?.items)) return r.data.items
  if (Array.isArray(r?.data?.data)) return r.data.data
  if (Array.isArray(r?.items)) return r.items
  if (Array.isArray(r?.data)) return r.data
  if (Array.isArray(r?.users)) return r.users
  if (Array.isArray(r?.clients)) return r.clients
  if (Array.isArray(r?.projects)) return r.projects
  return []
}

const PRIORITY_LABEL: Record<TaskPriority, string> = {
  LOW: 'Bassa', MEDIUM: 'Media', HIGH: 'Alta', URGENT: 'Urgente',
}

const PRIORITY_VARIANT: Record<TaskPriority, 'neutral' | 'info' | 'warning' | 'danger'> = {
  LOW: 'neutral', MEDIUM: 'info', HIGH: 'warning', URGENT: 'danger',
}

const STATUS_LABEL: Record<string, string> = {
  TODO: 'Da Fare', IN_PROGRESS: 'In Corso', IN_REVIEW: 'In Review',
  DONE: 'Completato', CANCELLED: 'Cancellato', BLOCKED: 'Bloccato',
}

const STATUS_COLOR: Record<string, string> = {
  TODO: 'bg-slate-400', IN_PROGRESS: 'bg-primary-500', IN_REVIEW: 'bg-warning-500',
  DONE: 'bg-success-500', CANCELLED: 'bg-slate-300', BLOCKED: 'bg-danger-400',
}

const STATUS_TRANSITIONS: Record<string, TaskStatus[]> = {
  TODO: ['IN_PROGRESS'],
  IN_PROGRESS: ['IN_REVIEW', 'DONE', 'TODO'],
  IN_REVIEW: ['DONE', 'IN_PROGRESS'],
  DONE: ['TODO'],
  CANCELLED: ['TODO'],
  BLOCKED: ['TODO', 'IN_PROGRESS'],
}

// --- Create Form ---

interface CreateTaskForm {
  title: string
  description: string
  projectId: string
  clientId: string
  assigneeId: string
  priority: TaskPriority
  dueDate: string
}

const EMPTY_FORM: CreateTaskForm = {
  title: '', description: '', projectId: '', clientId: '', assigneeId: '', priority: 'MEDIUM', dueDate: '',
}

// --- Main Page ---

export function TasksPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const [searchParams, setSearchParams] = useSearchParams()
  const filterClientId = searchParams.get('clientId') || ''
  const deepLinkTaskId = searchParams.get('taskId') || ''
  const deepLinkHandled = useRef('')
  const [search, setSearch] = useState('')
  const [onlyMine, setOnlyMine] = useState(false)
  const [unassignedDept, setUnassignedDept] = useState<string>('') // '' = off, 'ALL' = all unassigned, deptId = specific dept
  const [showDeptDropdown, setShowDeptDropdown] = useState(false)
  const [filterPriority, setFilterPriority] = useState<TaskPriority | ''>('')
  // Su mobile la vista lista e' molto piu' usabile del kanban a scroll orizzontale.
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>(
    () => (typeof window !== 'undefined' && window.innerWidth < 768 ? 'list' : 'kanban')
  )
  const [hideDone, setHideDone] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<CreateTaskForm>(EMPTY_FORM)
  const [createError, setCreateError] = useState<string | null>(null)

  // Selected task for detail panel
  const [selectedTask, setSelectedTask] = useState<Task | null>(null)
  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState<Partial<CreateTaskForm>>({})
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [pendingStatus, setPendingStatus] = useState<TaskStatus | null>(null)

  // Multi-selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [selectionMode, setSelectionMode] = useState(false)
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)
  const [bulkMoveDropdown, setBulkMoveDropdown] = useState(false)
  const [bulkAssignDropdown, setBulkAssignDropdown] = useState(false)
  const [bulkAssignSearch, setBulkAssignSearch] = useState('')

  // Quick-assign state
  const [quickAssignTaskId, setQuickAssignTaskId] = useState<string | null>(null)
  const [quickAssignPos, setQuickAssignPos] = useState({ x: 0, y: 0 })
  const [quickAssignSearch, setQuickAssignSearch] = useState('')
  // Detail panel reassign
  const [detailReassign, setDetailReassign] = useState(false)
  const [detailReassignSearch, setDetailReassignSearch] = useState('')
  const quickAssignRef = useRef<HTMLDivElement>(null)
  const quickAssignInputRef = useRef<HTMLInputElement>(null)

  // --- Client filter name lookup ---
  const { data: filterClientRaw } = useQuery({
    queryKey: ['client', filterClientId],
    queryFn: () => clientsApi.getById(filterClientId),
    enabled: Boolean(filterClientId),
    staleTime: 5 * 60_000,
  })
  const filterClientName = (() => {
    const raw = filterClientRaw as any
    const client = raw?.data?.data ?? raw?.data ?? raw
    return client?.name ?? ''
  })()

  // --- Lookup queries ---
  const lookupEnabled = showCreate || editMode
  const canAssign = (user?.role.level ?? 99) <= 3
  const isOperative = (user?.role.level ?? 99) >= 4
  const canDeleteTasks = user?.isSuperAdmin || (user?.role.level ?? 99) <= 3

  const { data: clientsRaw } = useQuery({
    queryKey: ['clients-lookup'],
    queryFn: () => clientsApi.list({ limit: 100 }),
    enabled: lookupEnabled,
    staleTime: 5 * 60_000,
  })
  const clients = extractArray(clientsRaw)

  const { data: projectsRaw } = useQuery({
    queryKey: ['projects-lookup'],
    queryFn: () => projectsApi.list({ limit: 100 }),
    enabled: lookupEnabled,
    staleTime: 5 * 60_000,
  })
  const projects = extractArray(projectsRaw)

  // Users always loaded for managers (quick-assign)
  const { data: usersRaw } = useQuery({
    queryKey: ['users-lookup'],
    queryFn: () => usersApi.list({ limit: 100 }),
    enabled: lookupEnabled || canAssign,
    staleTime: 5 * 60_000,
  })
  const users = extractArray(usersRaw)

  // Departments for "Da Assegnare" filter
  const { data: deptsRaw } = useQuery({
    queryKey: ['departments-lookup'],
    queryFn: () => departmentsApi.list(),
    enabled: canAssign,
    staleTime: 10 * 60_000,
  })
  const departments = extractArray(deptsRaw)

  // --- Tasks query ---
  const { data, isLoading, isError, refetch } = useQuery<PaginatedResponse<Task>>({
    queryKey: ['tasks', { search, priority: filterPriority, clientId: filterClientId, onlyMine }],
    queryFn: () =>
      apiClient.get<PaginatedResponse<Task>>('/tasks', {
        search: search || undefined,
        priority: filterPriority || undefined,
        clientId: filterClientId || undefined,
        assigneeId: (onlyMine || isOperative) && user ? user.id : undefined,
        limit: 200,
      }),
    retry: false,
  })

  // --- Mutations ---
  const createMutation = useMutation({
    mutationFn: (d: Record<string, unknown>) => apiClient.post('/tasks', d),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setShowCreate(false)
      setForm(EMPTY_FORM)
      setCreateError(null)
    },
    onError: (err: any) => {
      setCreateError(err?.response?.data?.error?.message ?? err?.message ?? 'Errore nella creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...d }: Record<string, unknown> & { id: string }) =>
      apiClient.put(`/tasks/${id}`, d),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setEditMode(false)
      // Update selectedTask with response data so detail panel reflects changes
      const updated = (res as any)?.data ?? (res as any)
      if (updated && selectedTask) {
        setSelectedTask({ ...selectedTask, ...updated })
      }
    },
  })

  const statusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: TaskStatus }) =>
      apiClient.patch(`/tasks/${taskId}/status`, { status }),
    onMutate: async ({ taskId, status }) => {
      // Optimistic update — aggiorna TUTTE le entry ['tasks'] (key dinamica con
      // search/priority/clientId/onlyMine), non solo una key parziale.
      queryClient.setQueriesData<any>(
        { queryKey: ['tasks'] },
        (old: any) => {
          if (!old) return old
          const items = old.items ?? old.data
          if (!Array.isArray(items)) return old
          const mapped = items.map((t: Task) => (t.id === taskId ? { ...t, status } : t))
          return old.items ? { ...old, items: mapped } : { ...old, data: mapped }
        }
      )
      // Update selected task too
      if (selectedTask?.id === taskId) {
        setSelectedTask((prev) => prev ? { ...prev, status } : prev)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (taskId: string) => apiClient.delete(`/tasks/${taskId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setSelectedTask(null)
      setDeleteConfirm(false)
    },
    // L'errore e' gia gestito globalmente con un toast.
  })

  // --- Bulk mutations ---
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => apiClient.delete(`/tasks/${id}`)))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setSelectedIds(new Set())
      setSelectionMode(false)
      setBulkDeleteConfirm(false)
    },
    onError: (err: any) => {
      console.error('Bulk delete failed:', err)
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const bulkStatusMutation = useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: TaskStatus }) => {
      await Promise.all(ids.map((id) => apiClient.patch(`/tasks/${id}/status`, { status })))
    },
    onMutate: async ({ ids, status }) => {
      const idSet = new Set(ids)
      queryClient.setQueriesData<any>(
        { queryKey: ['tasks'] },
        (old: any) => {
          if (!old) return old
          const items = old.items ?? old.data
          if (!Array.isArray(items)) return old
          const mapped = items.map((t: Task) => (idSet.has(t.id) ? { ...t, status } : t))
          return old.items ? { ...old, items: mapped } : { ...old, data: mapped }
        }
      )
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setSelectedIds(new Set())
      setSelectionMode(false)
      setBulkMoveDropdown(false)
    },
  })

  // --- Assign mutation (single) ---
  const assignMutation = useMutation({
    mutationFn: ({ taskId, assigneeId }: { taskId: string; assigneeId: string }) =>
      apiClient.post(`/tasks/${taskId}/assign`, { assigneeId }),
    onSuccess: async (_res, vars) => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setQuickAssignTaskId(null)
      setQuickAssignSearch('')
      // Reload fresh task data for detail panel
      if (selectedTask && selectedTask.id === vars.taskId) {
        try {
          const fresh = await apiClient.get<any>(`/tasks/${vars.taskId}`)
          const taskData = fresh?.data ?? fresh
          if (taskData?.id) setSelectedTask(taskData)
        } catch { /* fallback: invalidateQueries already refreshed the list */ }
      }
    },
  })

  // --- Bulk assign mutation ---
  const bulkAssignMutation = useMutation({
    mutationFn: async ({ ids, assigneeId }: { ids: string[]; assigneeId: string }) => {
      await Promise.all(ids.map((id) => apiClient.post(`/tasks/${id}/assign`, { assigneeId })))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      setSelectedIds(new Set())
      setSelectionMode(false)
      setBulkAssignDropdown(false)
      setBulkAssignSearch('')
    },
  })

  // --- Handlers ---

  // Quick-assign: open popup near click
  function handleAssignClick(taskId: string, e: React.MouseEvent) {
    e.stopPropagation()
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    setQuickAssignPos({ x: rect.left, y: rect.bottom + 4 })
    setQuickAssignTaskId(taskId)
    setQuickAssignSearch('')
    setTimeout(() => quickAssignInputRef.current?.focus(), 50)
  }

  // Close quick-assign on outside click
  useEffect(() => {
    if (!quickAssignTaskId) return
    function handleClickOutside(e: MouseEvent) {
      if (quickAssignRef.current && !quickAssignRef.current.contains(e.target as Node)) {
        setQuickAssignTaskId(null)
        setQuickAssignSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [quickAssignTaskId])

  // Close panel / open dropdowns with Escape (chiude il layer piu' interno per primo)
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      if (quickAssignTaskId) {
        setQuickAssignTaskId(null)
        setQuickAssignSearch('')
      } else if (showDeptDropdown) {
        setShowDeptDropdown(false)
      } else if (bulkMoveDropdown || bulkAssignDropdown) {
        setBulkMoveDropdown(false)
        setBulkAssignDropdown(false)
      } else if (detailReassign) {
        setDetailReassign(false)
        setDetailReassignSearch('')
      } else if (selectedTask) {
        setSelectedTask(null)
        setEditMode(false)
        setDeleteConfirm(false)
        setPendingStatus(null)
        setDetailReassign(false)
        setDetailReassignSearch('')
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [quickAssignTaskId, showDeptDropdown, bulkMoveDropdown, bulkAssignDropdown, detailReassign, selectedTask])

  const toggleSelect = useCallback((taskId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(taskId)) {
        next.delete(taskId)
      } else {
        next.add(taskId)
      }
      // Exit selection mode if nothing selected
      if (next.size === 0) setSelectionMode(false)
      return next
    })
  }, [])

  function exitSelectionMode() {
    setSelectionMode(false)
    setSelectedIds(new Set())
    setBulkDeleteConfirm(false)
    setBulkMoveDropdown(false)
  }

  function handleBulkMove(taskIds: string[], newStatus: TaskStatus) {
    bulkStatusMutation.mutate({ ids: taskIds, status: newStatus })
  }

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault()
    setCreateError(null)
    if (!form.title.trim()) { setCreateError('Il titolo e\' obbligatorio'); return }
    if (!form.projectId && !form.clientId) { setCreateError('Seleziona un progetto o un cliente'); return }
    createMutation.mutate({
      title: form.title.trim(),
      description: form.description || undefined,
      projectId: form.projectId || undefined,
      clientId: form.clientId || undefined,
      assigneeId: form.assigneeId || undefined,
      priority: form.priority,
      dueDate: form.dueDate || undefined,
    })
  }

  function handleTaskClick(task: Task, e?: React.MouseEvent) {
    // Cmd/Ctrl+Click: toggle selection without needing selectionMode
    if (e && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (!selectionMode) setSelectionMode(true)
      toggleSelect(task.id)
      return
    }

    // In selection mode, toggle selection instead of opening detail
    if (selectionMode) {
      toggleSelect(task.id)
      return
    }

    setSelectedTask(task)
    setEditMode(false)
    setDeleteConfirm(false)
    setPendingStatus(null)
    setDetailReassign(false)
    setDetailReassignSearch('')
  }

  function openEditMode() {
    if (!selectedTask) return
    setEditForm({
      title: selectedTask.title,
      description: selectedTask.description ?? '',
      priority: selectedTask.priority,
      assigneeId: selectedTask.assignee?.id ?? selectedTask.assigneeId ?? '',
      clientId: selectedTask.client?.id ?? (selectedTask as any).clientId ?? '',
      projectId: selectedTask.project?.id ?? selectedTask.projectId ?? '',
      dueDate: selectedTask.dueDate ? selectedTask.dueDate.slice(0, 10) : '',
    })
    setEditMode(true)
  }

  function handleEditSave() {
    if (!selectedTask) return
    updateMutation.mutate({
      id: selectedTask.id,
      title: editForm.title?.trim() || selectedTask.title,
      description: editForm.description || undefined,
      priority: editForm.priority || selectedTask.priority,
      assigneeId: editForm.assigneeId || undefined,
      clientId: editForm.clientId || undefined,
      projectId: editForm.projectId || undefined,
      dueDate: editForm.dueDate || undefined,
    })
  }

  function handleStatusChange(taskId: string, newStatus: TaskStatus) {
    statusMutation.mutate({ taskId, status: newStatus })
  }

  // --- Derived data ---
  const rawItems = data?.items ?? data?.data ?? []
  const tasks = rawItems.filter((t) => {
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false
    if (filterPriority && t.priority !== filterPriority) return false
    if (t.status === 'CANCELLED') return false
    if (hideDone && t.status === 'DONE') return false
    if (unassignedDept) {
      if ((t as any).assigneeId) return false // must be unassigned
      if (unassignedDept !== 'ALL' && (t as any).departmentId !== unassignedDept) return false // specific dept
    }
    return true
  })

  const totalCount = data?.total ?? data?.pagination?.total ?? rawItems.length

  // Deep link: auto-select task from ?taskId=xxx
  useEffect(() => {
    if (!deepLinkTaskId || deepLinkHandled.current === deepLinkTaskId) return
    deepLinkHandled.current = deepLinkTaskId

    // Remove taskId from URL
    const next = new URLSearchParams(searchParams)
    next.delete('taskId')
    setSearchParams(next, { replace: true })

    // Fetch the specific task and open detail panel
    apiClient.get<any>(`/tasks/${deepLinkTaskId}`).then((res) => {
      const task = res?.data ?? res
      if (task?.id) {
        setSelectedTask(task)
        setEditMode(false)
      }
    }).catch(() => {})
  }, [deepLinkTaskId])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Task</h1>
          <p className="text-sm text-slate-500 mt-1">
            Panoramica di tutte le attivita' (<span className="tabular-nums">{totalCount}</span> totali)
          </p>
        </div>
      </div>

      {/* Search + filters + view toggle */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Cerca task..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg w-52 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 placeholder:text-slate-400"
          />
        </div>

        {/* Priority filters */}
        <div className="flex items-center gap-1.5">
          {(Object.entries(PRIORITY_LABEL) as [TaskPriority, string][]).map(([value, label]) => (
            <button
              key={value}
              onClick={() => setFilterPriority(filterPriority === value ? '' : value)}
              className={cn('transition-opacity', filterPriority && filterPriority !== value && 'opacity-40')}
            >
              <Badge variant={filterPriority === value ? PRIORITY_VARIANT[value] : 'neutral'} size="sm">
                {label}
              </Badge>
            </button>
          ))}
        </div>

        {/* Hide done toggle */}
        <button
          onClick={() => setHideDone(!hideDone)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors',
            hideDone
              ? 'bg-primary-50 border-primary-200 text-primary-700'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
          )}
        >
          <Archive className="w-3.5 h-3.5" />
          Nascondi completati
        </button>

        {/* Selection mode toggle */}
        <button
          onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
          className={cn(
            'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors',
            selectionMode
              ? 'bg-primary-50 border-primary-200 text-primary-700'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
          )}
        >
          <MousePointerClick className="w-3.5 h-3.5" />
          Seleziona
        </button>

        {/* Le Mie Task toggle — visible for L1/L2/L3 */}
        {user && user.role.level <= 3 && (
          <button
            onClick={() => setOnlyMine(!onlyMine)}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
              onlyMine
                ? 'bg-primary-600 border-primary-600 text-white'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            )}
          >
            <UserCheck className="w-3.5 h-3.5" />
            Le Mie Task
          </button>
        )}

        {/* Da Assegnare dropdown — visible for L1/L2/L3 */}
        {user && user.role.level <= 3 && (
          <div className="relative">
            <button
              onClick={() => setShowDeptDropdown(!showDeptDropdown)}
              className={cn(
                'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                unassignedDept
                  ? 'bg-warning-50 border-warning-200 text-warning-600'
                  : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
              )}
            >
              <UserPlus className="w-3.5 h-3.5" />
              {unassignedDept
                ? unassignedDept === 'ALL'
                  ? 'Da Assegnare (Tutti)'
                  : `Da Assegnare: ${departments.find((d: any) => d.id === unassignedDept)?.name ?? ''}`
                : 'Da Assegnare'}
              <ChevronDown className="w-3 h-3" />
            </button>
            {showDeptDropdown && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowDeptDropdown(false)} />
                <div className="absolute top-full left-0 mt-1.5 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-40 py-1">
                  {unassignedDept && (
                    <button
                      className="w-full text-left px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 font-medium"
                      onClick={() => { setUnassignedDept(''); setShowDeptDropdown(false) }}
                    >
                      Mostra tutti i task
                    </button>
                  )}
                  <button
                    className={cn(
                      'w-full text-left px-3 py-2 text-xs hover:bg-slate-50',
                      unassignedDept === 'ALL' ? 'bg-warning-50 text-warning-600 font-semibold' : 'text-slate-700'
                    )}
                    onClick={() => { setUnassignedDept('ALL'); setShowDeptDropdown(false) }}
                  >
                    Tutti i reparti
                  </button>
                  <div className="border-t border-slate-100 my-1" />
                  {departments.map((dept: any) => (
                    <button
                      key={dept.id}
                      className={cn(
                        'w-full text-left px-3 py-2 text-xs hover:bg-slate-50',
                        unassignedDept === dept.id ? 'bg-warning-50 text-warning-600 font-semibold' : 'text-slate-700'
                      )}
                      onClick={() => { setUnassignedDept(dept.id); setShowDeptDropdown(false) }}
                    >
                      {dept.name}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex items-center gap-0.5 ml-auto p-1 bg-slate-100 rounded-full">
          <button
            onClick={() => setViewMode('kanban')}
            className={cn('p-1.5 rounded-full transition-colors', viewMode === 'kanban' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-400 hover:text-slate-600')}
            aria-label="Vista kanban"
            aria-pressed={viewMode === 'kanban'}
          >
            <Columns className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={cn('p-1.5 rounded-full transition-colors', viewMode === 'list' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-400 hover:text-slate-600')}
            aria-label="Vista lista"
            aria-pressed={viewMode === 'list'}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Client filter banner */}
      {filterClientId && (
        <div className="flex items-center gap-2 px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg text-sm">
          <span className="text-primary-700">
            Filtro attivo: task del cliente <strong className="font-semibold">{filterClientName || '...'}</strong>
          </span>
          <button
            onClick={() => setSearchParams({})}
            className="ml-auto flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-md hover:bg-primary-100 transition-colors"
          >
            <X className="w-3 h-3" />
            Rimuovi filtro
          </button>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex-1 min-w-[220px] space-y-2">
              <div className="h-8 bg-slate-100 rounded animate-pulse" />
              {Array.from({ length: 3 }).map((_, j) => (
                <div key={j} className="h-24 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card">
          <ErrorState
            title="Impossibile caricare i task"
            description="Si e' verificato un errore durante il caricamento. Riprova."
            onRetry={() => { void refetch() }}
          />
        </div>
      ) : tasks.length === 0 ? (
        <div className="flex flex-col items-center justify-center min-h-64 bg-white rounded-2xl border border-slate-200 shadow-card">
          <div className="flex items-center justify-center w-12 h-12 bg-slate-50 rounded-xl mb-4">
            <CheckSquare className="w-6 h-6 text-slate-300" />
          </div>
          <h3 className="text-base font-semibold text-slate-900 mb-1">
            {search || filterPriority ? 'Nessun task trovato' : 'Nessun task ancora'}
          </h3>
          <p className="text-sm text-slate-500">
            {search || filterPriority ? 'Prova a modificare i filtri' : 'Crea il primo task per iniziare'}
          </p>
        </div>
      ) : viewMode === 'kanban' ? (
        <TaskKanban
          tasks={tasks}
          onTaskMove={handleStatusChange}
          onTaskClick={handleTaskClick}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
          selectionMode={selectionMode}
          onBulkMove={handleBulkMove}
          onAssignClick={canAssign ? handleAssignClick : undefined}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-x-auto">
          <div className="min-w-[640px]">
          <div className={cn(
            'grid gap-4 px-4 py-2.5 border-b border-slate-200 bg-slate-50',
            selectionMode
              ? 'grid-cols-[28px_1fr_100px_100px_100px_80px]'
              : 'grid-cols-[1fr_100px_100px_100px_80px]'
          )}>
            {selectionMode && <span />}
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Task</span>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Priorita'</span>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Stato</span>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Assegnato</span>
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">Scadenza</span>
          </div>
          <div className="divide-y divide-slate-200">
            {tasks.map((task) => {
              const isRowSelected = selectedIds.has(task.id)
              return (
              <div
                key={task.id}
                onClick={(e) => handleTaskClick(task, e)}
                className={cn(
                  'grid gap-4 px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer items-center border-l-2 border-transparent',
                  selectionMode
                    ? 'grid-cols-[28px_1fr_100px_100px_100px_80px]'
                    : 'grid-cols-[1fr_100px_100px_100px_80px]',
                  isRowSelected && 'bg-primary-50 border-primary-500',
                  !isRowSelected && selectedTask?.id === task.id && 'bg-primary-50 border-primary-500'
                )}
              >
                {selectionMode && (
                  <div className={cn(
                    'w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors flex-shrink-0',
                    isRowSelected
                      ? 'bg-primary-600 border-primary-600'
                      : 'bg-white border-slate-200'
                  )}>
                    {isRowSelected && (
                      <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="2,6 5,9 10,3" />
                      </svg>
                    )}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{task.title}</p>
                  {task.client && (
                    <p className="text-xs text-slate-400 truncate">{task.client.name}</p>
                  )}
                </div>
                <Badge variant={PRIORITY_VARIANT[task.priority]} size="sm">
                  {PRIORITY_LABEL[task.priority]}
                </Badge>
                <div className="flex items-center gap-1.5">
                  <div className={cn('w-1.5 h-1.5 rounded-full', STATUS_COLOR[task.status] ?? 'bg-slate-300')} />
                  <span className="text-xs text-slate-600">{STATUS_LABEL[task.status] ?? task.status}</span>
                </div>
                <div
                  className={cn(
                    'flex items-center gap-1.5',
                    canAssign && 'cursor-pointer hover:bg-primary-50 rounded-md px-1 -mx-1 transition-colors'
                  )}
                  onClick={canAssign ? (e) => handleAssignClick(task.id, e) : undefined}
                >
                  {task.assignee ? (
                    <>
                      <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                        <span className="text-[9px] font-semibold text-white">
                          {task.assignee.firstName?.charAt(0) ?? '?'}
                        </span>
                      </div>
                      <span className="text-xs text-slate-600 truncate">{task.assignee.firstName}</span>
                    </>
                  ) : canAssign ? (
                    <div className="flex items-center gap-1 text-primary-600">
                      <UserPlus className="w-3.5 h-3.5" />
                      <span className="text-xs">Assegna</span>
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">{'\u2014'}</span>
                  )}
                </div>
                <span className="text-xs text-slate-500 tabular-nums">
                  {task.dueDate
                    ? new Date(task.dueDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })
                    : '\u2014'}
                </span>
              </div>
              )
            })}
          </div>
          </div>
        </div>
      )}

      {/* ============ BULK ACTIONS BAR ============ */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-center pb-4 px-4 pointer-events-none">
          <div className="flex items-center gap-3 px-5 py-3 bg-slate-900 text-white rounded-xl shadow-lg pointer-events-auto">
            <span className="text-sm font-medium">
              {selectedIds.size} task selezionat{selectedIds.size === 1 ? 'a' : 'e'}
            </span>

            <div className="w-px h-5 bg-slate-600" />

            {/* Move to dropdown */}
            <div className="relative">
              <button
                onClick={() => setBulkMoveDropdown(!bulkMoveDropdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700 hover:bg-slate-600 rounded-md transition-colors"
              >
                Sposta in
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
              {bulkMoveDropdown && (
                <div className="absolute bottom-full mb-2 left-0 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[160px]">
                  {(Object.entries(STATUS_LABEL) as [TaskStatus, string][])
                    .filter(([key]) => key !== 'CANCELLED')
                    .map(([status, label]) => (
                      <button
                        key={status}
                        onClick={() => {
                          bulkStatusMutation.mutate({ ids: Array.from(selectedIds), status })
                        }}
                        disabled={bulkStatusMutation.isPending}
                        className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors flex items-center gap-2"
                      >
                        <div className={cn('w-2 h-2 rounded-full', STATUS_COLOR[status])} />
                        {label}
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Bulk assign dropdown */}
            {canAssign && (
              <div className="relative">
                <button
                  onClick={() => { setBulkAssignDropdown(!bulkAssignDropdown); setBulkAssignSearch('') }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                >
                  <UserPlus className="w-3.5 h-3.5" />
                  Assegna a
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {bulkAssignDropdown && (
                  <div className="absolute bottom-full mb-2 left-0 bg-white border border-slate-200 rounded-xl shadow-lg min-w-[200px] overflow-hidden">
                    <div className="p-2 border-b border-slate-100">
                      <input
                        type="text"
                        placeholder="Cerca membro..."
                        value={bulkAssignSearch}
                        onChange={(e) => setBulkAssignSearch(e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto py-1">
                      {users
                        .filter((u: any) => {
                          if (!bulkAssignSearch) return true
                          const name = `${u.firstName} ${u.lastName}`.toLowerCase()
                          return name.includes(bulkAssignSearch.toLowerCase())
                        })
                        .map((u: any) => (
                          <button
                            key={u.id}
                            onClick={() => bulkAssignMutation.mutate({ ids: Array.from(selectedIds), assigneeId: u.id })}
                            disabled={bulkAssignMutation.isPending}
                            className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-primary-50 transition-colors flex items-center gap-2"
                          >
                            <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                              <span className="text-[9px] font-semibold text-white">{u.firstName?.charAt(0)}</span>
                            </div>
                            {u.firstName} {u.lastName}
                          </button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Delete — only L3+ */}
            {canDeleteTasks && (!bulkDeleteConfirm ? (
              <button
                onClick={() => setBulkDeleteConfirm(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-danger-600 hover:bg-danger-700 rounded-md transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Elimina
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-xs text-danger-300">Confermi?</span>
                <button
                  onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
                  disabled={bulkDeleteMutation.isPending}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-danger-600 hover:bg-danger-700 rounded-md transition-colors"
                >
                  {bulkDeleteMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                  Si, elimina
                </button>
                <button
                  onClick={() => setBulkDeleteConfirm(false)}
                  className="px-2 py-1.5 text-xs font-medium text-slate-300 hover:text-white transition-colors"
                >
                  No
                </button>
              </div>
            ))}

            <div className="w-px h-5 bg-slate-600" />

            {/* Cancel selection */}
            <button
              onClick={exitSelectionMode}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white transition-colors"
            >
              <X className="w-3.5 h-3.5" />
              Annulla
            </button>
          </div>
        </div>
      )}

      {/* ============ TASK DETAIL SLIDE-OVER ============ */}
      {selectedTask && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => { setSelectedTask(null); setEditMode(false); setDeleteConfirm(false); setPendingStatus(null); setDetailReassign(false); setDetailReassignSearch('') }}
          />

          {/* Panel */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label={`Dettaglio task: ${selectedTask.title}`}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-lg border-l border-slate-200 z-50 flex flex-col overflow-hidden animate-in slide-in-from-right duration-200"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <div className={cn('w-2.5 h-2.5 rounded-full', STATUS_COLOR[selectedTask.status] ?? 'bg-slate-300')} />
                <span className="text-sm font-medium text-slate-600">
                  {STATUS_LABEL[selectedTask.status] ?? selectedTask.status}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {!editMode && (
                  <button
                    onClick={openEditMode}
                    className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 transition-colors"
                    title="Modifica"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
                {canDeleteTasks && (
                <button
                  onClick={() => setDeleteConfirm(true)}
                  className="p-1.5 rounded-md hover:bg-danger-100 text-slate-500 hover:text-danger-600 transition-colors"
                  title="Elimina"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                )}
                <button
                  onClick={() => { setSelectedTask(null); setEditMode(false); setDeleteConfirm(false); setPendingStatus(null); setDetailReassign(false); setDetailReassignSearch('') }}
                  className="p-1.5 rounded-md hover:bg-slate-200 text-slate-400 transition-colors"
                  aria-label="Chiudi pannello"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Delete confirmation */}
            {deleteConfirm && (
              <div className="px-5 py-3 bg-danger-50 border-b border-danger-200 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-danger-500 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-danger-800">Eliminare questa task?</p>
                  <p className="text-xs text-danger-600">L'azione non puo' essere annullata.</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(selectedTask.id) }}
                    disabled={deleteMutation.isPending}
                    className="px-3 py-1.5 text-xs font-medium bg-danger-600 text-white rounded-lg hover:bg-danger-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {deleteMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                    Elimina
                  </button>
                </div>
              </div>
            )}

            {/* Body - scrollable */}
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
              {/* Title */}
              {editMode ? (
                <input
                  type="text"
                  value={editForm.title ?? ''}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full text-lg font-semibold text-slate-900 border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                />
              ) : (
                <h2 className="text-lg font-semibold text-slate-900 leading-snug">
                  {selectedTask.title}
                </h2>
              )}

              {/* Info grid */}
              <div className="space-y-3">
                {/* Client */}
                {(selectedTask.client || editMode) && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-slate-500 w-20 flex-shrink-0">Cliente</span>
                    {editMode ? (
                      <select
                        value={editForm.clientId ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, clientId: e.target.value })}
                        className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                      >
                        <option value="">Nessuno</option>
                        {clients.map((c: any) => (
                          <option key={c.id} value={c.id}>{c.name ?? c.companyName ?? c.id}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm text-slate-800">{selectedTask.client?.name ?? '\u2014'}</span>
                    )}
                  </div>
                )}

                {/* Project */}
                {(selectedTask.project || selectedTask.client || editMode) && (
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-slate-500 w-20 flex-shrink-0">Progetto</span>
                    {editMode ? (
                      <select
                        value={editForm.projectId ?? ''}
                        onChange={(e) => setEditForm({ ...editForm, projectId: e.target.value })}
                        className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                      >
                        <option value="">Nessuno</option>
                        {projects
                          .filter((p: any) => !editForm.clientId || p.clientId === editForm.clientId)
                          .map((p: any) => (
                            <option key={p.id} value={p.id}>{p.name ?? p.id}</option>
                          ))}
                      </select>
                    ) : (
                      <span className="text-sm text-slate-800">{selectedTask.project?.name ?? '\u2014'}</span>
                    )}
                  </div>
                )}

                {/* Priority */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-500 w-20 flex-shrink-0">Priorita'</span>
                  {editMode ? (
                    <select
                      value={editForm.priority ?? selectedTask.priority}
                      onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as TaskPriority })}
                      className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                    >
                      <option value="LOW">Bassa</option>
                      <option value="MEDIUM">Media</option>
                      <option value="HIGH">Alta</option>
                      <option value="URGENT">Urgente</option>
                    </select>
                  ) : (
                    <Badge variant={PRIORITY_VARIANT[selectedTask.priority]} size="sm">
                      {PRIORITY_LABEL[selectedTask.priority]}
                    </Badge>
                  )}
                </div>

                {/* Assignee */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-500 w-20 flex-shrink-0">Assegnato</span>
                  {editMode ? (
                    <select
                      value={editForm.assigneeId ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, assigneeId: e.target.value })}
                      className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                    >
                      <option value="">Nessuno</option>
                      {users.map((u: any) => (
                        <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                      ))}
                    </select>
                  ) : canAssign ? (
                    <div className="relative">
                      <button
                        onClick={() => setDetailReassign(!detailReassign)}
                        className="flex items-center gap-2 hover:bg-slate-100 rounded-md px-2 py-1 -mx-2 -my-1 transition-colors group"
                        title="Clicca per riassegnare"
                      >
                        {selectedTask.assignee ? (
                          <>
                            <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center">
                              <span className="text-[10px] font-semibold text-white">
                                {selectedTask.assignee.firstName?.charAt(0) ?? '?'}
                              </span>
                            </div>
                            <span className="text-sm text-slate-800">
                              {selectedTask.assignee.firstName} {selectedTask.assignee.lastName}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm text-slate-400 italic">Non assegnato</span>
                        )}
                        <Edit3 className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                      {detailReassign && (
                        <div className="absolute left-0 top-full mt-1.5 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 max-h-60 overflow-y-auto">
                          <div className="px-3 py-1.5">
                            <input
                              type="text"
                              placeholder="Cerca utente..."
                              value={detailReassignSearch}
                              onChange={(e) => setDetailReassignSearch(e.target.value)}
                              className="w-full text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                              autoFocus
                            />
                          </div>
                          {users
                            .filter((u: any) =>
                              !detailReassignSearch ||
                              `${u.firstName} ${u.lastName}`.toLowerCase().includes(detailReassignSearch.toLowerCase())
                            )
                            .map((u: any) => (
                              <button
                                key={u.id}
                                onClick={() => {
                                  assignMutation.mutate({ taskId: selectedTask.id, assigneeId: u.id })
                                  setDetailReassign(false)
                                  setDetailReassignSearch('')
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-primary-50 transition-colors flex items-center gap-2"
                              >
                                <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center">
                                  <span className="text-[9px] font-semibold text-white">{u.firstName?.charAt(0)}</span>
                                </div>
                                {u.firstName} {u.lastName}
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  ) : selectedTask.assignee ? (
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center">
                        <span className="text-[10px] font-semibold text-white">
                          {selectedTask.assignee.firstName?.charAt(0) ?? '?'}
                        </span>
                      </div>
                      <span className="text-sm text-slate-800">
                        {selectedTask.assignee.firstName} {selectedTask.assignee.lastName}
                      </span>
                    </div>
                  ) : (
                    <span className="text-sm text-slate-400 italic">Non assegnato</span>
                  )}
                </div>

                {/* Due date */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-500 w-20 flex-shrink-0">Scadenza</span>
                  {editMode ? (
                    <input
                      type="date"
                      value={editForm.dueDate ?? ''}
                      onChange={(e) => setEditForm({ ...editForm, dueDate: e.target.value })}
                      className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  ) : selectedTask.dueDate ? (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5 text-slate-400" />
                      <span className={cn(
                        'text-sm',
                        new Date(selectedTask.dueDate) < new Date() && selectedTask.status !== 'DONE'
                          ? 'text-danger-600 font-medium' : 'text-slate-800'
                      )}>
                        {new Date(selectedTask.dueDate).toLocaleDateString('it-IT', {
                          weekday: 'short', day: '2-digit', month: 'long', year: 'numeric',
                        })}
                      </span>
                      {new Date(selectedTask.dueDate) < new Date() && selectedTask.status !== 'DONE' && (
                        <Badge variant="danger" size="sm">In ritardo</Badge>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-slate-400 italic">Nessuna scadenza</span>
                  )}
                </div>

                {/* Created at */}
                <div className="flex items-center gap-3">
                  <span className="text-xs font-medium text-slate-500 w-20 flex-shrink-0">Creata il</span>
                  <span className="text-sm text-slate-600">
                    {new Date(selectedTask.createdAt).toLocaleDateString('it-IT', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div>
                <span className="text-xs font-medium text-slate-500 block mb-1.5">Descrizione</span>
                {editMode ? (
                  <textarea
                    value={editForm.description ?? ''}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    rows={4}
                    className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 resize-none"
                    placeholder="Aggiungi una descrizione..."
                  />
                ) : (
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {selectedTask.description || <span className="italic text-slate-400">Nessuna descrizione</span>}
                  </p>
                )}
              </div>

              {/* Edit buttons */}
              {editMode && (
                <div className="flex items-center gap-2 pt-2">
                  <button
                    onClick={handleEditSave}
                    disabled={updateMutation.isPending}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                  >
                    {updateMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                    Salva Modifiche
                  </button>
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              )}

              {/* Status change section */}
              {!editMode && (
                <div>
                  <span className="text-xs font-medium text-slate-500 block mb-2">Cambia stato</span>

                  {/* Confirmation bar */}
                  {pendingStatus && (
                    <div className="flex items-center gap-3 px-3 py-2.5 mb-3 rounded-lg border border-primary-200 bg-primary-50">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-800">
                          Spostare in <strong className="font-semibold">{STATUS_LABEL[pendingStatus]}</strong>?
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPendingStatus(null)}
                          className="px-3 py-1.5 text-xs font-medium bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700"
                        >
                          Annulla
                        </button>
                        <button
                          onClick={() => {
                            handleStatusChange(selectedTask.id, pendingStatus)
                            setPendingStatus(null)
                          }}
                          disabled={statusMutation.isPending}
                          className="px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
                        >
                          {statusMutation.isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                          Conferma
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {(STATUS_TRANSITIONS[selectedTask.status] ?? []).map((nextStatus) => (
                      <button
                        key={nextStatus}
                        onClick={() => setPendingStatus(nextStatus)}
                        disabled={statusMutation.isPending}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                          pendingStatus === nextStatus
                            ? 'bg-primary-50 border-primary-300 text-primary-700'
                            : nextStatus === 'DONE'
                            ? 'bg-success-50 border-success-300 text-success-700 hover:bg-success-100'
                            : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                        )}
                      >
                        <ChevronRight className="w-3 h-3" />
                        {STATUS_LABEL[nextStatus] ?? nextStatus}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* CREATE TASK MODAL rimosso — task si creano solo dentro i progetti */}
      {false && showCreate && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowCreate(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <form
              onSubmit={handleCreateSubmit}
              className="bg-white rounded-3xl shadow-modal w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
                <h2 className="text-base font-semibold text-slate-900">Nuovo Task</h2>
                <button type="button" onClick={() => setShowCreate(false)} className="p-1 rounded-md hover:bg-slate-100 text-slate-400">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-5 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Titolo *</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Es. Revisione landing page"
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Descrizione</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={3}
                    placeholder="Dettagli del task..."
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Cliente</label>
                    <select
                      value={form.clientId}
                      onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                    >
                      <option value="">Seleziona...</option>
                      {clients.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name ?? c.companyName ?? c.id}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Progetto</label>
                    <select
                      value={form.projectId}
                      onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                    >
                      <option value="">Seleziona...</option>
                      {projects
                        .filter((p: any) => !form.clientId || p.clientId === form.clientId)
                        .map((p: any) => (
                          <option key={p.id} value={p.id}>{p.name ?? p.id}</option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Assegnato a</label>
                    <select
                      value={form.assigneeId}
                      onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                    >
                      <option value="">Nessuno</option>
                      {users.map((u: any) => (
                        <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Priorita'</label>
                    <select
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value as TaskPriority })}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 bg-white"
                    >
                      <option value="LOW">Bassa</option>
                      <option value="MEDIUM">Media</option>
                      <option value="HIGH">Alta</option>
                      <option value="URGENT">Urgente</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Scadenza</label>
                    <input
                      type="date"
                      value={form.dueDate}
                      onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
                    />
                  </div>
                </div>
                {createError && (
                  <div className="px-3 py-2 bg-danger-50 border border-danger-200 rounded-md text-sm text-danger-700">
                    {createError}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-3xl">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {createMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Crea Task
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ============ QUICK-ASSIGN FLOATING DROPDOWN ============ */}
      {quickAssignTaskId && (
        <div
          ref={quickAssignRef}
          className="fixed z-[60] bg-white border border-slate-200 rounded-xl shadow-lg min-w-[220px] overflow-hidden"
          style={{
            left: Math.min(quickAssignPos.x, window.innerWidth - 240),
            top: Math.min(quickAssignPos.y, window.innerHeight - 280),
          }}
        >
          <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">Assegna a</p>
            <input
              ref={quickAssignInputRef}
              type="text"
              placeholder="Cerca membro..."
              value={quickAssignSearch}
              onChange={(e) => setQuickAssignSearch(e.target.value)}
              className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
            />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {users
              .filter((u: any) => {
                if (!quickAssignSearch) return true
                const name = `${u.firstName} ${u.lastName}`.toLowerCase()
                return name.includes(quickAssignSearch.toLowerCase())
              })
              .map((u: any) => (
                <button
                  key={u.id}
                  onClick={() => assignMutation.mutate({ taskId: quickAssignTaskId, assigneeId: u.id })}
                  disabled={assignMutation.isPending}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-primary-50 transition-colors flex items-center gap-2.5"
                >
                  <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-semibold text-white">{u.firstName?.charAt(0)}</span>
                  </div>
                  <span>{u.firstName} {u.lastName}</span>
                </button>
              ))}
            {users.length === 0 && (
              <p className="px-3 py-2 text-xs text-slate-400">Nessun utente trovato</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
